import { useState } from "react";
import { getAuth, applyActionCode, verifyPasswordResetCode, confirmPasswordReset } from "firebase/auth";
import { useSearchParams, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import TextField from "@mui/material/TextField";

export default function Verify() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    const auth = getAuth();

    const urlMode = searchParams.get("mode");
    
    const oobCode = searchParams.get("oobCode");
    const [mode, setMode] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [validCode, setValidCode] = useState(false);
    const [loading, setLoading] = useState(false);

    // HARD GUARD
    if (!urlMode || !oobCode) {
        return <p>Invalid or missing verification link.</p>;
    }

    // HANDLE VERIFY / RESET STEP 1
    const handleAuth = async () => {
        if (loading) return;

        setLoading(true);

        try {
            // EMAIL VERIFY 
            if (urlMode === "verifyEmail") {
                await applyActionCode(auth, oobCode);

                await auth.currentUser?.reload();

                await Swal.fire({
                    icon: "success",
                    title: "Email Verified!",
                    text: "Your email has been successfully verified. This window will be closed after you confirm.",
                    confirmButtonColor: "#3085d6",
                }).then(async(result) => {
                    if (result.isConfirmed) {
                        window.close();
                    }
                });
            }

            //RESET PASSWORD STEP 1
            else if (urlMode === "resetPassword") {
                await verifyPasswordResetCode(auth, oobCode);

                setMode("resetPassword");
                setValidCode(true);
            }

        } catch (error) {
            await Swal.fire({
                icon: "error",
                title: "Error",
                text: error.message,
            });

            navigate("/login");
        } finally {
            setLoading(false);
        }
    };

    const handleResetPassword = async (e) => {
        e.preventDefault();

        if (loading) return;
        setLoading(true);

        try {
            if (newPassword.length < 6) {
                await Swal.fire({
                    icon: "error",
                    title: "Weak Password",
                    text: "Password must be at least 6 characters.",
                });
                return;
            }

            if (newPassword !== confirmPassword) {
                await Swal.fire({
                    icon: "error",
                    title: "Passwords Do Not Match",
                    text: "Please make sure both passwords match.",
                });
                return;
            }

            await confirmPasswordReset(auth, oobCode, newPassword);

            await Swal.fire({
                icon: "success",
                title: "Password Reset Successful",
                text: "You can now log in with your new password.",
            }).then(async(result) => {
                if (result.isConfirmed) {
                    window.close();
                }
            });

        } catch (error) {
            await Swal.fire({
                icon: "error",
                title: "Reset Failed",
                text: error.message,
            });

        } finally {
            setLoading(false);
        }
    };

    // UI
    return (
        <div style={{ padding: "20px", textAlign: "center" }}>

            {!mode && urlMode === "verifyEmail" && (
                <>
                    <h2>Verify Email</h2>
                    <p>Click below to verify your email address.</p>

                    <button className="verify-button" onClick={handleAuth} disabled={loading} >
                        {loading ? "Verifying..." : "Verify Email"}
                    </button>
                </>
            )}

            {!mode && urlMode === "resetPassword" && (
                <>
                    <h2>Reset Password</h2>
                    <p>Click below to continue password reset.</p>

                    <button className="verify-button" onClick={handleAuth} disabled={loading}>
                        {loading ? "Checking..." : "Continue"}
                    </button>
                </>
            )}

            {mode === "resetPassword" && validCode && (

                <div className="password-popup">
                    <div className="reset-pass-div">
                        <h2 id="change-pass-text">Reset Password</h2>
                            <form className="change-pass-form" onSubmit={handleResetPassword} name="change-password-form">
                                <TextField label="New Password" type="password" placeholder="" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required size="medium" sx={{
                                    "& .MuiInputLabel-root.Mui-focused": { color: "black" }, "& .MuiOutlinedInput-root": { "&.Mui-focused fieldset": { border: "1px solid black", 
                                        color: "black" }, }, }}/>
                
                                <TextField label="Confirm Password" type="password" placeholder="" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required 
                                    size="medium" sx={{ "& .MuiInputLabel-root.Mui-focused": { color: "black" }, "& .MuiOutlinedInput-root": { "&.Mui-focused fieldset": { border: "1px solid black", 
                                        color: "black"},},}}/>
                                <button className="auth-btn" type="submit" disabled={loading}> {loading ? "Updating..." : "Reset Password"} </button>
                            </form>
                                <p style={{ fontSize: "10px", textAlign: "left", marginLeft: "50px"}}>Your new password <strong><u>must</u></strong> be:</p>
                                <ul>
                                    <li>Be more than 6 characters long</li>
                                    <li>Include at least one <u style={{ fontWeight: "bold" }}>Uppercase character</u></li>
                                    <li>Include at least one <u style={{ fontWeight: "bold" }}>Lowercase character</u></li>
                                    <li>Include at least one <u style={{ fontWeight: "bold" }}>Special character</u></li>
                                </ul>
                                <p style={{ fontSize: "10px", textAlign: "left", marginLeft: "50px", color: "red"}}>Refrain from using easily guessable passwords</p>
                                <p style={{ fontSize: "10px", marginTop: "-8px", textAlign: "left", marginLeft: "50px", color: "red"}}>Do not use previously used passwords</p>
                            </div>
                        </div>
            
            )}

        </div>
    );
}